import { useCallback, useEffect, useMemo } from "react";
import { useUrlState } from "./useUrlState";
import type { FilterState } from "@/types";
import { DEFAULT_TIME_RANGE, type TimeRangeKey } from "@/config/timeRanges";

/**
 * Decode the global filter state from URL search params, and provide a
 * `setFilter` that round-trips back to the URL.
 *
 * Filter keys are restricted to the schema in `types/index.ts`. Anything
 * else in the URL is preserved (e.g. `tab=overview`, `metric=loginAbuse`)
 * but ignored by the filter API calls.
 */

const ARRAY_KEYS = new Set<keyof FilterState>([
  "riskBands",
  "endpointGroups",
]);

const NUM_KEYS = new Set<keyof FilterState>([
  "scoreMin", "scoreMax", "threatScoreMin", "threatScoreMax",
]);

const BOOL_KEYS = new Set<keyof FilterState>([
  "verifiedBot",
]);

const FILTER_KEYS: readonly (keyof FilterState)[] = [
  "timeRange", "customFromIso", "customToIso",
  "riskBands", "scoreMin", "scoreMax",
  "endpointGroups", "endpointType", "pathPrefix", "method",
  "country", "colo", "asn", "ip", "ja3", "ja4",
  "email", "businessId", "sessionId",
  "botScoreBucket", "threatScoreMin", "threatScoreMax", "verifiedBot",
  "managedList", "detectionId", "reasonContains", "userAgentContains",
  "action", "outcome",
] as const;

function decode(state: Record<string, string | undefined>): FilterState {
  const out: Partial<FilterState> = {};
  for (const k of FILTER_KEYS) {
    const v = state[k];
    if (v == null) continue;
    if (ARRAY_KEYS.has(k)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (out as any)[k] = v.split(",").filter(Boolean);
    } else if (NUM_KEYS.has(k)) {
      const n = Number(v);
      if (Number.isFinite(n)) (out as Record<string, number>)[k] = n;
    } else if (BOOL_KEYS.has(k)) {
      (out as Record<string, boolean>)[k] = v === "true";
    } else {
      (out as Record<string, string>)[k] = v;
    }
  }
  return {
    timeRange: (out.timeRange as TimeRangeKey) || DEFAULT_TIME_RANGE,
    ...out,
  } as FilterState;
}

export function useFilters(): {
  filters: FilterState;
  setFilter: <K extends keyof FilterState>(key: K, value: FilterState[K] | undefined) => void;
  patchFilters: (patch: Partial<FilterState>) => void;
  resetFilters: () => void;
} {
  const [state, patch] = useUrlState();

  const filters = useMemo(() => decode(state), [state]);

  const setFilter = useCallback(<K extends keyof FilterState>(key: K, value: FilterState[K] | undefined) => {
    let v: string | undefined;
    if (value == null) v = undefined;
    else if (Array.isArray(value)) v = value.length ? value.join(",") : undefined;
    else if (typeof value === "boolean") v = value ? "true" : "false";
    else v = String(value);
    patch({ [key]: v });
  }, [patch]);

  const patchFilters = useCallback((p: Partial<FilterState>) => {
    const next: Record<string, string | undefined> = {};
    for (const [k, value] of Object.entries(p)) {
      if (value == null) next[k] = undefined;
      else if (Array.isArray(value)) next[k] = value.length ? value.join(",") : undefined;
      else if (typeof value === "boolean") next[k] = value ? "true" : "false";
      else next[k] = String(value);
    }
    patch(next);
  }, [patch]);

  const resetFilters = useCallback(() => {
    const clear: Record<string, string | undefined> = {};
    for (const k of FILTER_KEYS) clear[k] = undefined;
    patch(clear);
  }, [patch]);

  // On first mount, ensure timeRange is set so card links produce
  // shareable URLs.
  useEffect(() => {
    if (!state.timeRange) patch({ timeRange: DEFAULT_TIME_RANGE }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { filters, setFilter, patchFilters, resetFilters };
}
